import React from 'react';
import * as PropTypes from 'prop-types';
import Chart from '../Charts/Chart';
import ChartErrorBoundary from '../Charts/ChartErrorBoundary';
import ChartWindow from '../Charts/ChartWindow';
import Draggable from '../DragAndDrop/Draggable';
import './ChartCollection.sass';
import SyncedChartGroup from './SyncedChartGroup';
import {
  CHART_SIZE_DEFAULT,
  CHART_SIZE_LARGE,
  SYNCED_CHART_SIZE_DEFAULT,
} from '../../constants/common';

const ChartCollection = ({
  unityIsLoading,
  inSimulation,
  toggleGraphics,
  resizeChart,
  bubbleChart,
  charts,
  dims,
}) => {
  // Don't display charts until the scene is finished loading
  if (unityIsLoading) {
    return <></>;
  }

  const chartGroups = charts.reduce((groups, chart) => {
    if (chart.hasOwnProperty('sync') && chart.sync.length) {
      if (groups.hasOwnProperty(chart.sync)) {
        groups[chart.sync].push(chart);
      } else {
        groups[chart.sync] = [chart];
      }
    } else {
      groups[chart.id] = [chart];
    }
    return groups;
  }, {});

  return Object.values(chartGroups).map((charts) => {
    const [chart, ...secondaryCharts] = charts;
    const { position, coords, ...chartConfig } = chart;
    let handleResize, itemDims;

    if (!chartConfig.size) {
      if (secondaryCharts.length > 0) {
        chartConfig.size = SYNCED_CHART_SIZE_DEFAULT;
      } else {
        chartConfig.size = CHART_SIZE_DEFAULT;
      }
    }

    if (secondaryCharts.length > 0) {
      itemDims = [
        chartConfig.size[0],
        chartConfig.size[1] +
          secondaryCharts.reduce(
            (acc, elem) =>
              acc + (elem.height ? elem.height : chartConfig.size[1]),
            0
          ),
      ];

      handleResize = (size) =>
        resizeChart(
          chartConfig.id,
          size === SYNCED_CHART_SIZE_DEFAULT
            ? CHART_SIZE_DEFAULT
            : SYNCED_CHART_SIZE_DEFAULT
        );
    } else {
      itemDims = chartConfig.size;
      handleResize = (size) =>
        resizeChart(
          chartConfig.id,
          size === CHART_SIZE_DEFAULT ? CHART_SIZE_LARGE : CHART_SIZE_DEFAULT
        );
    }

    return (
      <Draggable
        key={chartConfig.id}
        id={chartConfig.id}
        itemType="CHART"
        position={position}
        coords={coords}
        containerDims={dims}
        bubbleItem={bubbleChart}
        itemDims={itemDims}
      >
        <ChartWindow
          draggable={!inSimulation}
          resizable={!inSimulation && !chartConfig.hasOwnSize}
          size={chartConfig.size}
          handleClose={() => toggleGraphics(chartConfig.node)}
          handleResize={handleResize}
        >
          <ChartErrorBoundary>
            {secondaryCharts.length > 0 ? (
              <SyncedChartGroup
                size={chartConfig.size}
                charts={[chartConfig, ...secondaryCharts]}
              />
            ) : (
              <Chart {...chartConfig} />
            )}
          </ChartErrorBoundary>
        </ChartWindow>
      </Draggable>
    );
  });
};

ChartCollection.propTypes = {
  charts: PropTypes.array,
  dims: PropTypes.array,
  toggleGraphics: PropTypes.func.isRequired,
  bubbleChart: PropTypes.func.isRequired,
};
ChartCollection.defaultProps = {
  charts: [],
  dims: {
    width: 1,
    height: 1,
  },
};

export default ChartCollection;
